import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from 'antd';
import moment from 'moment';
import { useStores } from '@src/components/store';
import { EmptyState } from '@src/components/shared/empty-state/empty-state';
import { IconType } from '@src/components/shared/custom-icon/custom-icon';
import { EventType } from '@src/dtos/analytics.dto';
import { WhatsNewEntry } from '@src/dtos/whats-new.dto';
import { WHATS_NEW_ENTRIES } from '../data/whats-new-data';
import { filterToRetentionWindow } from '../data/whats-new-utils';
import { WhatsNewDetailModal } from '../detail-modal/WhatsNewDetailModal';
import './whatsNewList.scss';

function formatDate(isoDate: string): string {
  return moment(isoDate).format('MMM D, YYYY');
}

interface WhatsNewListProps {
  onSeeWhatsNew?: (entry: WhatsNewEntry) => void;
}

export const WhatsNewList: React.FC<WhatsNewListProps> = observer(({ onSeeWhatsNew }) => {
  const { notificationStore } = useStores();
  const [selectedEntry, setSelectedEntry] = useState<WhatsNewEntry | null>(null);

  const entries = filterToRetentionWindow(WHATS_NEW_ENTRIES);

  const handleSeeWhatsNew = (entry: WhatsNewEntry) => {
    notificationStore.sendGeneralEvent(EventType.LinkClicked, {
      button: 'seeWhatsNew',
      location: 'notificationsArea',
    });
    if (onSeeWhatsNew) {
      onSeeWhatsNew(entry);
    } else {
      setSelectedEntry(entry);
    }
  };

  if (entries.length === 0) {
    return (
      <EmptyState
        iconType={IconType.NOTIFICATION_EMPTY_STATE}
        title="No new updates"
      />
    );
  }

  return (
    <div className="whats-new-list">
      {entries.map(entry => {
        const isUnread =
          !notificationStore.whatsNewLastSeenDate ||
          moment.utc(entry.releaseDate).isAfter(notificationStore.whatsNewLastSeenDate);

        return (
          <div key={entry.version} className="whats-new-item" data-testid="whats-new-item">
            {isUnread && <span className="whats-new-item__unread-dot" aria-hidden="true" />}

            <div className="whats-new-item__header">
              <span
                className={`whats-new-item__version${isUnread ? ' whats-new-item__version--unread' : ''}`}
                data-testid="whats-new-item-version"
              >
                Version {entry.version}
              </span>
              <span className={`whats-new-item__date${isUnread ? ' whats-new-item__date--unread' : ''}`}>
                {formatDate(entry.releaseDate)}
              </span>
            </div>

            <div className="whats-new-item__description">{entry.previewDescription}</div>

            <div className="whats-new-item__actions">
              <Button
                className="custom-btn secondary-btn"
                data-testid="whats-new-see-button"
                onClick={() => handleSeeWhatsNew(entry)}
              >
                {`See what's new`}
              </Button>
              {entry.releaseNotesUrl && <a
                href={entry.releaseNotesUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="whats-new-item__release-notes"
                data-testid="whats-new-release-notes-link"
              >
                Release notes
              </a>}
            </div>
          </div>
        );
      })}

      <WhatsNewDetailModal entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
    </div>
  );
});
